/**
 * System Admin Console — Cycle 6 §4 (SYSTEM_ADMIN multi-tenant 관리).
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import {
  listTenants, changeTenantStatus, approveOnboarding, rejectOnboarding,
  type TenantListItem, type TenantStatus,
} from '../../api/systemAdmin';
import { getSharedLang, getDateLocale, type Lang } from '../../lib/base-i18n';
import { layerConfirm, layerPrompt } from '../../components/LayerPopup/LayerPopup';
import '../AdminConsolePage/AdminConsolePage.css';

// Cycle 53 — local i18n dict (cycle 49 MfaVerifyPage / cycle 52 LoginPage 패턴 일관).
// 29 strings × 3 lang (ko/en/ja). fallback chain: lang → en → ko.
type StatusKey = TenantStatus | 'all';
const SYS_MESSAGES: Record<Lang, {
  filter_label: string;
  status_filter: Record<StatusKey, string>;
  status_label: Record<TenantStatus, string>;
  err_fetch: string;
  err_reason_too_short: string;
  err_concurrent: string;
  err_status_change_failed: string;
  err_invalid_transition_prefix: string;
  denied_heading: string;
  denied_body: string;
  sidebar_title: string;
  sidebar_subtitle: string;
  nav_audit: string;
  nav_ai_gateway: string;
  nav_drift_opt_out: string;
  main_heading: string;
  main_subheading: string;
  count_suffix: string;
  loading: string;
  empty_tenants: string;
  aria_table: string;
  col_company: string;
  col_domain: string;
  col_status: string;
  col_signup_date: string;
  col_flags: string;
  col_actions: string;
  btn_suspend: string;
  btn_restore: string;
  btn_archive: string;
  // Cycle 73 — 승인 큐
  btn_approve: string;
  btn_reject: string;
  confirm_reject: string;
  reason_prompt: string;
}> = {
  ko: {
    filter_label: '상태 필터:',
    status_filter: {
      all: '전체', active: '활성', pending: '가입 진행 중', pending_approval: '승인 대기',
      suspended: '일시 중지', archived: '보관됨', frozen: '동결됨',
    },
    status_label: { active: '활성', pending: '가입 중', pending_approval: '승인 대기', suspended: '일시중지', archived: '보관', frozen: '동결' },
    err_fetch: '테넌트 목록 불러오기 실패',
    err_reason_too_short: '사유는 10자 이상 입력해 주세요.',
    err_concurrent: '다른 관리자가 동시에 변경 — 새로고침 후 다시 시도.',
    err_status_change_failed: '상태 변경 실패',
    err_invalid_transition_prefix: '전이 불가',
    denied_heading: '접근 권한이 없습니다',
    denied_body: '이 페이지는 서비스 운영팀(SYSTEM_ADMIN) 만 접근할 수 있습니다.',
    sidebar_title: '서비스 운영',
    sidebar_subtitle: 'SYSTEM_ADMIN 전용 — 전체 테넌트 관리.',
    nav_audit: '📊 감사 대시보드',
    nav_ai_gateway: '⚙️ AI Gateway 환경 점검',
    nav_drift_opt_out: '📭 Drift 수신 거부 현황',
    main_heading: '테넌트 목록',
    main_subheading: '가입한 회사 (테넌트) 전체 조회 및 운영 상태 관리.',
    count_suffix: '개 표시',
    loading: '불러오는 중...',
    empty_tenants: '표시할 테넌트가 없습니다.',
    aria_table: '테넌트 목록',
    col_company: '회사명', col_domain: '도메인', col_status: '상태',
    col_signup_date: '가입일', col_flags: '플래그', col_actions: '작업',
    btn_suspend: '일시중지', btn_restore: '복구', btn_archive: '보관',
    btn_approve: '승인', btn_reject: '거부',
    confirm_reject: '이 회사 가입을 거부하면 되돌릴 수 없습니다(보관 처리 + 관리자 계정 비활성화). 계속하시겠습니까?',
    reason_prompt: '거부 사유를 10자 이상 입력하세요:',
  },
  en: {
    filter_label: 'Status filter:',
    status_filter: {
      all: 'All', active: 'Active', pending: 'Signing up', pending_approval: 'Awaiting approval',
      suspended: 'Suspended', archived: 'Archived', frozen: 'Frozen',
    },
    status_label: { active: 'Active', pending: 'Signup', pending_approval: 'Awaiting approval', suspended: 'Suspended', archived: 'Archived', frozen: 'Frozen' },
    err_fetch: 'Failed to load tenants',
    err_reason_too_short: 'Reason must be at least 10 characters.',
    err_concurrent: 'Another admin changed it concurrently — please refresh and try again.',
    err_status_change_failed: 'Status change failed',
    err_invalid_transition_prefix: 'Invalid transition',
    denied_heading: 'Access denied',
    denied_body: 'This page is restricted to SYSTEM_ADMIN (operations team).',
    sidebar_title: 'Service ops',
    sidebar_subtitle: 'SYSTEM_ADMIN only — manage all tenants.',
    nav_audit: '📊 Audit dashboard',
    nav_ai_gateway: '⚙️ AI Gateway env check',
    nav_drift_opt_out: '📭 Drift opt-out status',
    main_heading: 'Tenants',
    main_subheading: 'View and manage all signed-up tenants and their operational status.',
    count_suffix: ' shown',
    loading: 'Loading…',
    empty_tenants: 'No tenants to display.',
    aria_table: 'Tenants',
    col_company: 'Company', col_domain: 'Domain', col_status: 'Status',
    col_signup_date: 'Signed up', col_flags: 'Flags', col_actions: 'Actions',
    btn_suspend: 'Suspend', btn_restore: 'Restore', btn_archive: 'Archive',
    btn_approve: 'Approve', btn_reject: 'Reject',
    confirm_reject: 'Rejecting this company sign-up is irreversible (archived + admin account deactivated). Continue?',
    reason_prompt: 'Enter rejection reason (10+ characters):',
  },
  ja: {
    filter_label: 'ステータスフィルタ:',
    status_filter: {
      all: '全て', active: '有効', pending: '加入中', pending_approval: '承認待ち',
      suspended: '一時停止', archived: '保管', frozen: '凍結',
    },
    status_label: { active: '有効', pending: '加入中', pending_approval: '承認待ち', suspended: '一時停止', archived: '保管', frozen: '凍結' },
    err_fetch: 'テナント一覧取得失敗',
    err_reason_too_short: '理由は10文字以上入力してください。',
    err_concurrent: '別の管理者が同時に変更 — 再読み込みして再試行してください。',
    err_status_change_failed: 'ステータス変更失敗',
    err_invalid_transition_prefix: '遷移不可',
    denied_heading: 'アクセス権限がありません',
    denied_body: 'このページは運営チーム(SYSTEM_ADMIN)のみアクセス可能です。',
    sidebar_title: 'サービス運営',
    sidebar_subtitle: 'SYSTEM_ADMIN 専用 — 全テナント管理。',
    nav_audit: '📊 監査ダッシュボード',
    nav_ai_gateway: '⚙️ AI Gateway 環境点検',
    nav_drift_opt_out: '📭 Drift 受信拒否状況',
    main_heading: 'テナント一覧',
    main_subheading: '加入した会社(テナント)全体照会と運営状態管理。',
    count_suffix: '件表示',
    loading: '読み込み中...',
    empty_tenants: '表示するテナントがありません。',
    aria_table: 'テナント一覧',
    col_company: '会社名', col_domain: 'ドメイン', col_status: 'ステータス',
    col_signup_date: '加入日', col_flags: 'フラグ', col_actions: 'アクション',
    btn_suspend: '一時停止', btn_restore: '復旧', btn_archive: '保管',
    btn_approve: '承認', btn_reject: '拒否',
    confirm_reject: 'この会社の加入を拒否すると元に戻せません(保管 + 管理者アカウント無効化)。続行しますか?',
    reason_prompt: '拒否理由を10文字以上入力してください:',
  },
};

function tSys(): typeof SYS_MESSAGES['ko'] {
  return SYS_MESSAGES[getSharedLang()] ?? SYS_MESSAGES.en;
}

// Cycle 53 — STATUS_FILTER_OPTIONS / STATUS_LABEL 의 hardcoded ko 는 tSys() 에서 lang-aware
// (위 SYS_MESSAGES dict). filter/label 의 enum key 는 컴파일 시점 SSOT 유지.
// Cycle 73 — 'pending_approval' 추가(승인 큐 필터 옵션). 이 배열이 select option 을 생성하므로
// 미추가 시 운영자가 승인 대기만 필터링 불가(adversary scenario 1). 'pending' 과 별개 유지.
const STATUS_FILTER_VALUES: ReadonlyArray<StatusKey> = ['all', 'active', 'pending', 'pending_approval', 'suspended', 'archived', 'frozen'];

const STATUS_COLOR: Record<TenantStatus, string> = {
  active: 'var(--color-success, #15803d)',
  pending: 'var(--color-text-secondary, #6b7280)',
  pending_approval: 'var(--color-warn, #d97706)',  // Cycle 73 — 승인 대기(운영자 액션 필요) 강조
  suspended: 'var(--color-warn, #d97706)',
  archived: 'var(--color-text-secondary, #6b7280)',
  frozen: 'var(--color-danger, #b91c1c)',
};

export function SystemAdminPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [items, setItems] = useState<TenantListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<TenantStatus | 'all'>('all');
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  async function reload() {
    setLoading(true);
    setError(null);
    try {
      const r = await listTenants({ status: filter === 'all' ? undefined : filter, limit: 50 });
      setItems(r.items);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? tSys().err_fetch);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void reload(); }, [filter]);

  async function handleStatusChange(t: TenantListItem, newStatus: 'active' | 'suspended' | 'archived') {
    const labels = tSys().status_label;
    const reason = await layerPrompt(
      `${t.name} (${t.domain}) ${labels[t.status]} → ${labels[newStatus]}\n(${tSys().err_reason_too_short})`,
    );
    if (!reason || reason.trim().length < 10) {
      setError(tSys().err_reason_too_short);
      return;
    }
    setBusyId(t.id);
    setError(null);
    try {
      await changeTenantStatus(t.id, { status: newStatus, reason: reason.trim() });
      void reload();
    } catch (e: any) {
      const data = e?.response?.data;
      if (data?.code === 'INVALID_TRANSITION') setError(`${tSys().err_invalid_transition_prefix}: ${data.message}`);
      else if (data?.code === 'CONCURRENT_UPDATE') setError(tSys().err_concurrent);
      else setError(data?.message ?? tSys().err_status_change_failed);
    } finally {
      setBusyId(null);
    }
  }

  // Cycle 73 — 회사 가입 승인 (pending_approval → active). body 없음, 즉시.
  async function handleApprove(t: TenantListItem) {
    setBusyId(t.id);
    setError(null);
    try {
      await approveOnboarding(t.id);
      void reload();
    } catch (e: any) {
      const data = e?.response?.data;
      if (data?.code === 'CONCURRENT_UPDATE') setError(tSys().err_concurrent);
      else setError(data?.message ?? tSys().err_status_change_failed);
    } finally {
      setBusyId(null);
    }
  }

  // Cycle 73 — 회사 가입 거부 (pending_approval → archived + admin 강등). 복구 불가라 confirm 필수.
  async function handleReject(t: TenantListItem) {
    if (!(await layerConfirm(tSys().confirm_reject, { danger: true }))) return;  // 되돌릴 수 없음 경고
    setBusyId(t.id);  // prompt/await 동안 이중 거부 차단 (Auditor: setBusyId 를 prompt 전)
    const reason = await layerPrompt(tSys().reason_prompt);
    if (reason === null) { setBusyId(null); return; }  // 취소 = 무동작 (단문 에러와 분리)
    if (reason.trim().length < 10) { setBusyId(null); setError(tSys().err_reason_too_short); return; }
    setError(null);
    try {
      await rejectOnboarding(t.id, { reason: reason.trim() });
      void reload();
    } catch (e: any) {
      const data = e?.response?.data;
      if (data?.code === 'CONCURRENT_UPDATE') setError(tSys().err_concurrent);
      else setError(data?.message ?? tSys().err_status_change_failed);
    } finally {
      setBusyId(null);
    }
  }

  const i18n = tSys();

  if (!user || user.role !== 'SYSTEM_ADMIN') {
    return (
      <div className="admin-console admin-console--denied" role="alert">
        <h2>{i18n.denied_heading}</h2>
        <p>{i18n.denied_body}</p>
      </div>
    );
  }

  return (
    <div className="admin-console">
      <aside className="admin-console__sidebar">
        <h1 className="admin-console__title">{i18n.sidebar_title}</h1>
        <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary, #6b7280)' }}>
          {i18n.sidebar_subtitle}
        </p>
        <nav style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          <Link to="/system-admin/audit" className="admin-console__btn" style={{ textAlign: 'center', textDecoration: 'none' }}>
            {i18n.nav_audit}
          </Link>
          <Link to="/system-admin/ai-gateway-config" className="admin-console__btn" style={{ textAlign: 'center', textDecoration: 'none' }}>
            {i18n.nav_ai_gateway}
          </Link>
          <Link to="/system-admin/drift-opt-outs" className="admin-console__btn" style={{ textAlign: 'center', textDecoration: 'none' }}>
            {i18n.nav_drift_opt_out}
          </Link>
        </nav>
      </aside>

      <main className="admin-console__content">
        <h2 className="admin-console__heading">{i18n.main_heading}</h2>
        <p className="admin-console__subheading">
          {i18n.main_subheading}
        </p>

        {error && <div className="admin-console__alert admin-console__alert--warn" role="alert">{error}</div>}

        <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <label htmlFor="status-filter" style={{ fontSize: '0.875rem' }}>{i18n.filter_label}</label>
          <select
            id="status-filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value as any)}
            className="admin-console__input"
            style={{ width: 'auto' }}
          >
            {STATUS_FILTER_VALUES.map((v) => (
              <option key={v} value={v}>{i18n.status_filter[v]}</option>
            ))}
          </select>
          <span style={{ marginLeft: 'auto', fontSize: '0.8125rem', color: 'var(--color-text-secondary, #6b7280)' }}>
            {items.length}{i18n.count_suffix}
          </span>
        </div>

        {loading && <p>{i18n.loading}</p>}
        {!loading && items.length === 0 && <p>{i18n.empty_tenants}</p>}
        {!loading && items.length > 0 && (
          <table className="admin-console__table" aria-label={i18n.aria_table}>
            <thead>
              <tr>
                <th>{i18n.col_company}</th>
                <th>{i18n.col_domain}</th>
                <th>{i18n.col_status}</th>
                <th>{i18n.col_signup_date}</th>
                <th>{i18n.col_flags}</th>
                <th>{i18n.col_actions}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((t) => (
                <tr
                  key={t.id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/system-admin/tenants/${t.id}`)}
                >
                  <td>
                    <strong>{t.name}</strong>
                    <br />
                    <code style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary, #6b7280)' }}>
                      {t.code}
                    </code>
                  </td>
                  <td>{t.domain ?? '—'}</td>
                  <td>
                    <span style={{ color: STATUS_COLOR[t.status], fontWeight: 600 }}>
                      {i18n.status_label[t.status]}
                    </span>
                  </td>
                  <td>{new Date(t.createdAt).toLocaleDateString(getDateLocale())}</td>
                  <td>
                    {t.graceActive && <span style={{ fontSize: '0.75rem', marginRight: '0.25rem' }}>⏱ grace</span>}
                    {t.frozen && <span style={{ fontSize: '0.75rem', color: 'var(--color-danger, #b91c1c)' }}>🧊 frozen</span>}
                  </td>
                  <td>
                    {t.status === 'active' && (
                      <>
                        <button
                          type="button"
                          className="admin-console__btn admin-console__btn--danger"
                          disabled={busyId === t.id}
                          onClick={() => void handleStatusChange(t, 'suspended')}
                          style={{ marginRight: '0.25rem', fontSize: '0.75rem' }}
                        >
                          {i18n.btn_suspend}
                        </button>
                      </>
                    )}
                    {t.status === 'suspended' && (
                      <>
                        <button
                          type="button"
                          className="admin-console__btn admin-console__btn--primary"
                          disabled={busyId === t.id}
                          onClick={() => void handleStatusChange(t, 'active')}
                          style={{ marginRight: '0.25rem', fontSize: '0.75rem' }}
                        >
                          {i18n.btn_restore}
                        </button>
                        <button
                          type="button"
                          className="admin-console__btn admin-console__btn--danger"
                          disabled={busyId === t.id}
                          onClick={() => void handleStatusChange(t, 'archived')}
                          style={{ fontSize: '0.75rem' }}
                        >
                          {i18n.btn_archive}
                        </button>
                      </>
                    )}
                    {/* Cycle 73 — 승인 큐: pending_approval 행에 승인/거부. onClick stopPropagation
                        으로 <tr onClick=navigate(상세)> 와 충돌 차단 (adversary scenario 3). */}
                    {t.status === 'pending_approval' && (
                      <>
                        <button
                          type="button"
                          className="admin-console__btn admin-console__btn--primary"
                          disabled={busyId === t.id}
                          onClick={(e) => { e.stopPropagation(); void handleApprove(t); }}
                          style={{ marginRight: '0.25rem', fontSize: '0.75rem' }}
                        >
                          {i18n.btn_approve}
                        </button>
                        <button
                          type="button"
                          className="admin-console__btn admin-console__btn--danger"
                          disabled={busyId === t.id}
                          onClick={(e) => { e.stopPropagation(); void handleReject(t); }}
                          style={{ fontSize: '0.75rem' }}
                        >
                          {i18n.btn_reject}
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </main>
    </div>
  );
}
